/**
 * Typed API client for the Quotes Platform backend.
 *
 * Auto-injects the access token from localStorage and handles 401
 * by attempting a refresh before failing.
 */

const API_BASE =
  process.env.NEXT_PUBLIC_API_URL || "/api/v1";

class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
  }
}

/** Try to refresh the access token using the stored refresh token. */
async function tryRefresh(): Promise<string | null> {
  const refresh = localStorage.getItem("refresh_token");
  if (!refresh) return null;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    return data.access_token as string;
  } catch {
    return null;
  }
}

interface FetchOptions extends RequestInit {
  /** Override token (default: auto-inject from localStorage). */
  token?: string;
  /** Skip auto-injecting token (for public endpoints like login). */
  noAuth?: boolean;
}

async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { token, noAuth, ...fetchOpts } = opts;

  // Determine auth token
  const authToken = noAuth ? undefined : (token ?? localStorage.getItem("access_token") ?? undefined);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    ...(opts.headers as Record<string, string>),
  };

  let res = await fetch(`${API_BASE}${path}`, { ...fetchOpts, headers });

  // On 401, try refreshing the token once
  if (res.status === 401 && !noAuth && !path.includes("/auth/")) {
    const newToken = await tryRefresh();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      res = await fetch(`${API_BASE}${path}`, { ...fetchOpts, headers });
    } else {
      // Refresh failed — clear tokens but don't hard-redirect (preserves wizard state)
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      throw new ApiError(401, "Session expired. Please log in again.");
    }
  }

  if (res.status === 204) return {} as T;

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(res.status, body.detail || res.statusText);
  }

  return res.json();
}

export const api = {
  get: <T>(path: string, token?: string) =>
    request<T>(path, { method: "GET", token }),

  post: <T>(path: string, body: unknown, token?: string) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body), token }),

  put: <T>(path: string, body: unknown, token?: string) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body), token }),

  patch: <T>(path: string, body: unknown, token?: string) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body), token }),

  del: <T>(path: string, token?: string) =>
    request<T>(path, { method: "DELETE", token }),
};

export { ApiError };
